import React from 'react';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import {
  array,
  arrayOf,
  bool,
  func,
  node,
  number,
  object,
  oneOfType,
  shape,
  string,
} from 'prop-types';
import loadable from '@loadable/component';
import classNames from 'classnames';
import omit from 'lodash/omit';

import { intlShape, injectIntl, FormattedMessage } from '../../util/reactIntl';
import { displayPrice } from '../../util/configHelpers';
import {
  propTypes,
  LISTING_STATE_CLOSED,
  LINE_ITEM_NIGHT,
  LINE_ITEM_DAY,
  LINE_ITEM_ITEM,
  LINE_ITEM_HOUR,
} from '../../util/types';
import { formatMoney } from '../../util/currency';
import { parse, stringify } from '../../util/urlHelpers';
import { userDisplayNameAsString } from '../../util/data';
import { getProNetworkPriceLabel } from '../../util/proNetworkPricing';
import { getOfferedDayRateMoney, getRentalDisplayPrice } from '../../util/rentalPricing';
import { BUYING_SELLING_HELP_URL } from '../../util/rentalBookingDates';
import {
  INQUIRY_PROCESS_NAME,
  getSupportedProcessesInfo,
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

import { ModalInMobile, PrimaryButton, AvatarSmall, H1, H2 } from '../../components';

import css from './OrderPanel.module.css';

const BookingTimeForm = loadable(() =>
  import(/* webpackChunkName: "BookingTimeForm" */ './BookingTimeForm/BookingTimeForm')
);
const BookingDatesForm = loadable(() =>
  import(/* webpackChunkName: "BookingDatesForm" */ './BookingDatesForm/BookingDatesForm')
);
const InquiryWithoutPaymentForm = loadable(() =>
  import(
    /* webpackChunkName: "InquiryWithoutPaymentForm" */ './InquiryWithoutPaymentForm/InquiryWithoutPaymentForm'
  )
);
const ProductOrderForm = loadable(() =>
  import(/* webpackChunkName: "ProductOrderForm" */ './ProductOrderForm/ProductOrderForm')
);

// This defines when ModalInMobile shows content as Modal
const MODAL_BREAKPOINT = 1023;
const TODAY = new Date();

const priceData = (listing, price, currency, intl) => {
  const proNetworkPriceLabel = getProNetworkPriceLabel(listing, intl);
  if (proNetworkPriceLabel) {
    return { formattedPrice: proNetworkPriceLabel, priceTitle: proNetworkPriceLabel };
  }

  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

const openOrderModal = (isOwnListing, isClosed, history, location) => {
  if (isOwnListing || isClosed) {
    window.scrollTo(0, 0);
  } else {
    const { pathname, search, state } = location;
    const searchString = `?${stringify({ ...parse(search), orderOpen: true })}`;
    history.push(`${pathname}${searchString}`, state);
  }
};

const closeOrderModal = (history, location) => {
  const { pathname, search, state } = location;
  const searchParams = omit(parse(search), 'orderOpen');
  const searchString = `?${stringify(searchParams)}`;
  history.push(`${pathname}${searchString}`, state);
};

const handleSubmit = (
  isOwnListing,
  isClosed,
  isInquiryWithoutPayment,
  onSubmit,
  history,
  location
) => {
  // TODO: currently, inquiry-process does not have any form to ask more order data.
  // We can submit without opening any inquiry/order modal.
  return isInquiryWithoutPayment
    ? () => onSubmit({})
    : () => openOrderModal(isOwnListing, isClosed, history, location);
};

const dateFormattingOptions = { month: 'short', day: 'numeric', weekday: 'short' };

const PriceMaybe = props => {
  const { listing, price, publicData, validListingTypes, intl } = props;
  const { listingType } = publicData || {};

  const isInquiry = listingType === 'inquiry';
  const isBooking = listingType === 'daily-booking' || publicData?.unitType === 'day';
  const proNetworkPriceLabel = getProNetworkPriceLabel(listing, intl);
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice || (!price && !proNetworkPriceLabel)) {
    return null;
  }

  const rentalDisplay = isBooking && !isInquiry ? getRentalDisplayPrice(listing) : null;
  const displayMoney = rentalDisplay?.money || price;
  const formattedPrice = proNetworkPriceLabel || formatMoney(intl, displayMoney);
  const perUnitType = isInquiry ? '' : rentalDisplay?.unitType || 'week';
  const dayRateMoney = !isInquiry ? getOfferedDayRateMoney(listing) : null;
  const showRentalTiles = isBooking && !isInquiry && !proNetworkPriceLabel && (dayRateMoney || price);
  const showSaleTile = isInquiry && !proNetworkPriceLabel && price;

  if (showRentalTiles) {
    return (
      <div className={css.rateBlock}>
        <div className={css.rateBadge}>
          <FormattedMessage
            id={dayRateMoney ? 'OrderPanel.rateBadgeDual' : 'OrderPanel.rateBadgeWeekly'}
          />
        </div>
        <div className={css.priceTiles}>
          {dayRateMoney ? (
            <div className={classNames(css.priceTile, css.priceTileDay)}>
              <div className={css.priceTileAmount}>{formatMoney(intl, dayRateMoney)}</div>
              <div className={css.priceTileUnit}>
                <FormattedMessage id="OrderPanel.perUnit" values={{ unitType: 'day' }} />
              </div>
            </div>
          ) : null}
          {price ? (
            <div
              className={classNames(
                css.priceTile,
                dayRateMoney ? css.priceTileWeek : classNames(css.priceTileDay, css.priceTileSolo)
              )}
            >
              <div className={css.priceTileAmount}>{formatMoney(intl, price)}</div>
              <div className={css.priceTileUnit}>
                <FormattedMessage id="OrderPanel.perUnit" values={{ unitType: 'week' }} />
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  if (showSaleTile) {
    return (
      <div className={css.rateBlock}>
        <div className={css.rateBadge}>
          <FormattedMessage id="OrderPanel.rateBadgeSale" />
        </div>
        <div className={css.priceTiles}>
          <div className={classNames(css.priceTile, css.priceTileDay, css.priceTileSolo)}>
            <div className={css.priceTileAmount}>{formatMoney(intl, price)}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={css.priceContainer}>
      <p className={css.price}>{formattedPrice}</p>
      {proNetworkPriceLabel ? null : (
        <div className={css.perUnit}>
          <FormattedMessage
            id="OrderPanel.perUnit"
            values={{ unitType: perUnitType }}
          />
        </div>
      )}
    </div>
  );
};

const OrderPanel = props => {
  const {
    rootClassName,
    className,
    titleClassName,
    listing,
    validListingTypes,
    lineItemUnitType: lineItemUnitTypeMaybe,
    isOwnListing,
    onSubmit,
    title,
    titleDesktop,
    author,
    authorLink,
    onManageDisableScrolling,
    onFetchTimeSlots,
    monthlyTimeSlots,
    history,
    location,
    intl,
    onFetchTransactionLineItems,
    onContactUser,
    lineItems,
    marketplaceCurrency,
    dayCountAvailableForBooking,
    marketplaceName,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    addons
  } = props;

  const publicData = listing?.attributes?.publicData || {};
  const { unitType, transactionProcessAlias = '' } = publicData || {};
  const isProNetworkListing = publicData?.listingType === 'pro-network';
  const proNetworkWebsiteLink = publicData?.websiteLink;
  const proNetworkWebsiteUrl = proNetworkWebsiteLink
    ? /^https?:\/\//i.test(proNetworkWebsiteLink)
      ? proNetworkWebsiteLink
      : `https://${proNetworkWebsiteLink}`
    : null;
  const hasProNetworkWebsiteLink = !!proNetworkWebsiteUrl;
  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const lineItemUnitType = lineItemUnitTypeMaybe || `line-item/${unitType}`;

  const price = listing?.attributes?.price;
  const isPaymentProcess = processName !== INQUIRY_PROCESS_NAME;

  const showPriceMissing = isPaymentProcess && !price;
  const PriceMissing = () => {
    return (
      <p className={css.error}>
        <FormattedMessage id="OrderPanel.listingPriceMissing" />
      </p>
    );
  };
  const showInvalidCurrency = isPaymentProcess && price?.currency !== marketplaceCurrency;
  const InvalidCurrency = () => {
    return (
      <p className={css.error}>
        <FormattedMessage id="OrderPanel.listingCurrencyInvalid" />
      </p>
    );
  };

  const timeZone = listing?.attributes?.availabilityPlan?.timezone;
  const isClosed = listing?.attributes?.state === LISTING_STATE_CLOSED;

  const isBooking = isBookingProcess(processName);
  const shouldHaveBookingTime = isBooking && [LINE_ITEM_HOUR].includes(lineItemUnitType);
  const showBookingTimeForm = shouldHaveBookingTime && !isClosed && timeZone;

  const shouldHaveBookingDates =
    isBooking && [LINE_ITEM_DAY, LINE_ITEM_NIGHT].includes(lineItemUnitType);
  const showBookingDatesForm = shouldHaveBookingDates && !isClosed && timeZone;

  // The listing resource has a relationship: `currentStock`,
  // which you should include when making API calls.
  const isPurchase = isPurchaseProcess(processName);
  const currentStock = listing.currentStock?.attributes?.quantity;
  const isOutOfStock =
    !isProNetworkListing && isPurchase && lineItemUnitType === LINE_ITEM_ITEM && currentStock === 0;

  // Show form only when stock is fully loaded. This avoids "Out of stock" UI by
  // default before all data has been downloaded.
  const showProductOrderForm = isPurchase && typeof currentStock === 'number';

  const isInquiryProcess = processName === INQUIRY_PROCESS_NAME;
  const isProNetworkWebsiteFlow = isProNetworkListing && hasProNetworkWebsiteLink;
  const showInquiryForm = isInquiryProcess && !isProNetworkWebsiteFlow;
  const showProNetworkWebsiteButton = isInquiryProcess && isProNetworkWebsiteFlow;
  const inquiryCtaButtonMessageId = showProNetworkWebsiteButton
    ? 'OrderPanel.ctaButtonMessageVisitWebsite'
    : 'OrderPanel.ctaButtonMessageInquiry';

  const openProNetworkWebsite = () => {
    if (proNetworkWebsiteUrl) {
      window.open(proNetworkWebsiteUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const supportedProcessesInfo = getSupportedProcessesInfo();
  const isKnownProcess = supportedProcessesInfo.map(info => info.name).includes(processName);

  const { pickupEnabled, shippingEnabled } = listing?.attributes?.publicData || {};

  const showClosedListingHelpText = listing.id && isClosed;
  const proNetworkPriceLabel = getProNetworkPriceLabel(listing, intl);
  const rentalDisplay = isBooking ? getRentalDisplayPrice(listing) : { money: price, unitType };
  const displayPriceMoney = rentalDisplay.money || price;
  const displayUnitType = isBooking ? rentalDisplay.unitType || 'week' : unitType;
  const { formattedPrice, priceTitle } = priceData(
    listing,
    displayPriceMoney,
    marketplaceCurrency,
    intl
  );
  const isOrderOpen = !!parse(location.search).orderOpen;
  const submitOrder = values => {
    if (isOrderOpen) {
      closeOrderModal(history, location);
    }
    return onSubmit(values);
  };

  const subTitleText = showClosedListingHelpText
    ? intl.formatMessage({ id: 'OrderPanel.subTitleClosedListing' })
    : null;

  const authorDisplayName = userDisplayNameAsString(author, '');

  const classes = classNames(rootClassName || css.root, className);
  const titleClasses = classNames(titleClassName || css.orderTitle);
  const primaryButtonOnClick = showProNetworkWebsiteButton
    ? openProNetworkWebsite
    : handleSubmit(isOwnListing, isClosed, showInquiryForm, submitOrder, history, location);
  const useBookingCard = !isProNetworkListing && (showBookingDatesForm || showInquiryForm);

  return (
    <div className={classes}>
      <ModalInMobile
        className={css.orderModal}
        containerClassName={css.modalContainer}
        scrollLayerClassName={css.orderScrollLayer}
        id="OrderFormInModal"
        isModalOpenOnMobile={isOrderOpen}
        onClose={() => closeOrderModal(history, location)}
        showAsModalMaxWidth={MODAL_BREAKPOINT}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
      >
        <div className={useBookingCard ? css.bookingCard : null}>
        <div className={css.modalHeading}>
          <H1 className={css.heading}>{title}</H1>
        </div>

        <div className={css.orderHeading}>
          {titleDesktop ? titleDesktop : <H2 className={titleClasses}>{title}</H2>}
          {subTitleText ? <div className={css.orderHelp}>{subTitleText}</div> : null}
        </div>

        <div className={css.author}>
          <AvatarSmall user={author} className={css.providerAvatar} />
          <span>
            <FormattedMessage id="OrderPanel.author" values={{ name: authorLink || authorDisplayName }} />
          </span>
        </div>

        <PriceMaybe
          listing={listing}
          price={price}
          publicData={publicData}
          validListingTypes={validListingTypes}
          intl={intl}
        />

        {showPriceMissing ? (
          <PriceMissing />
        ) : showInvalidCurrency ? (
          <InvalidCurrency />
        ) : showBookingTimeForm ? (
          <BookingTimeForm
            className={css.bookingForm}
            formId="OrderPanelBookingTimeForm"
            lineItemUnitType={lineItemUnitType}
            onSubmit={submitOrder}
            price={price}
            marketplaceCurrency={marketplaceCurrency}
            dayCountAvailableForBooking={dayCountAvailableForBooking}
            listingId={listing.id}
            isOwnListing={isOwnListing}
            monthlyTimeSlots={monthlyTimeSlots}
            onFetchTimeSlots={onFetchTimeSlots}
            startDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
            endDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
            timeZone={timeZone}
            marketplaceName={marketplaceName}
            onFetchTransactionLineItems={onFetchTransactionLineItems}
            lineItems={lineItems}
            fetchLineItemsInProgress={fetchLineItemsInProgress}
            fetchLineItemsError={fetchLineItemsError}
            listing={listing}
          />
        ) : showBookingDatesForm ? (
          <BookingDatesForm
            className={css.bookingForm}
            formId="OrderPanelBookingDatesForm"
            lineItemUnitType={lineItemUnitType}
            onSubmit={submitOrder}
            price={price}
            marketplaceCurrency={marketplaceCurrency}
            dayCountAvailableForBooking={dayCountAvailableForBooking}
            listingId={listing.id}
            isOwnListing={isOwnListing}
            monthlyTimeSlots={monthlyTimeSlots}
            onFetchTimeSlots={onFetchTimeSlots}
            timeZone={timeZone}
            marketplaceName={marketplaceName}
            onFetchTransactionLineItems={onFetchTransactionLineItems}
            lineItems={lineItems}
            fetchLineItemsInProgress={fetchLineItemsInProgress}
            fetchLineItemsError={fetchLineItemsError}
            listing={listing}
          />
        ) : showProductOrderForm ? (
          <ProductOrderForm
            formId="OrderPanelProductOrderForm"
            onSubmit={submitOrder}
            price={price}
            marketplaceCurrency={marketplaceCurrency}
            currentStock={currentStock}
            pickupEnabled={pickupEnabled}
            shippingEnabled={shippingEnabled}
            listingId={listing.id}
            isOwnListing={isOwnListing}
            marketplaceName={marketplaceName}
            onFetchTransactionLineItems={onFetchTransactionLineItems}
            onContactUser={onContactUser}
            lineItems={lineItems}
            fetchLineItemsInProgress={fetchLineItemsInProgress}
            fetchLineItemsError={fetchLineItemsError}
            listing={listing}
          />
        ) : showProNetworkWebsiteButton ? (
          <PrimaryButton onClick={openProNetworkWebsite}>
            <FormattedMessage id="OrderPanel.ctaButtonMessageVisitWebsite" />
          </PrimaryButton>
        ) : showInquiryForm ? (
          <>
            <div className={css.iconRows}>
              <div className={css.iconRow}>
                <div className={css.iconBubble}>
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                    <line x1="7" y1="7" x2="7.01" y2="7" />
                  </svg>
                </div>
                <div>
                  <div className={css.iconRowTitle}>
                    <FormattedMessage id="InquiryWithoutPaymentForm.infoTitle" />
                  </div>
                  <div className={css.iconRowDesc}>
                    <FormattedMessage id="InquiryWithoutPaymentForm.infoDesc" />
                  </div>
                </div>
              </div>
            </div>
            <a
              className={css.learnMore}
              href={BUYING_SELLING_HELP_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FormattedMessage id="InquiryWithoutPaymentForm.learnMore" />
            </a>
            <InquiryWithoutPaymentForm formId="OrderPanelInquiryForm" onSubmit={submitOrder} />
          </>
        ) : !isKnownProcess ? (
          <p className={css.errorSidebar}>
            <FormattedMessage id="OrderPanel.unknownTransactionProcess" />
          </p>
        ) : null}
        </div>
      </ModalInMobile>
      <div className={css.openOrderForm}>
        <div className={css.priceContainerInCTA}>
          <div className={css.priceValue} title={priceTitle}>
            {formattedPrice}
          </div>
          {proNetworkPriceLabel || isInquiryProcess ? null : (
            <div className={css.perUnitInCTA}>
              <FormattedMessage id="OrderPanel.perUnit" values={{ unitType: displayUnitType }} />
            </div>
          )}
        </div>

        {isClosed ? (
          <div className={css.closedListingButton}>
            <FormattedMessage id="OrderPanel.closedListingButtonText" />
          </div>
        ) : (
          <PrimaryButton
            onClick={primaryButtonOnClick}
            disabled={isOutOfStock}
          >
            {isBooking ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessageRequestDates" />
            ) : isOutOfStock ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessageNoStock" />
            ) : isPurchase ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessagePurchase" />
            ) : (
              <FormattedMessage id={inquiryCtaButtonMessageId} />
            )}
          </PrimaryButton>
        )}
      </div>
    </div>
  );
};

OrderPanel.defaultProps = {
  rootClassName: null,
  className: null,
  titleClassName: null,
  isOwnListing: false,
  authorLink: null,
  titleDesktop: null,
  subTitle: null,
  monthlyTimeSlots: null,
  lineItems: null,
  fetchLineItemsError: null,
};

OrderPanel.propTypes = {
  rootClassName: string,
  className: string,
  titleClassName: string,
  listing: oneOfType([propTypes.listing, propTypes.ownListing]),
  validListingTypes: arrayOf(
    shape({
      listingType: string.isRequired,
      transactionType: shape({
        process: string.isRequired,
        alias: string.isRequired,
        unitType: string.isRequired,
      }).isRequired,
    })
  ).isRequired,
  isOwnListing: bool,
  author: oneOfType([propTypes.user, propTypes.currentUser]).isRequired,
  authorLink: node,
  onSubmit: func.isRequired,
  title: oneOfType([node, string]).isRequired,
  titleDesktop: node,
  subTitle: oneOfType([node, string]),
  onManageDisableScrolling: func.isRequired,

  onFetchTimeSlots: func.isRequired,
  monthlyTimeSlots: object,
  onFetchTransactionLineItems: func.isRequired,
  onContactUser: func,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
  marketplaceCurrency: string.isRequired,
  dayCountAvailableForBooking: number.isRequired,
  marketplaceName: string.isRequired,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default compose(
  withRouter,
  injectIntl
)(OrderPanel);
